import React, { useState } from 'react';
import { AdminChangePassword } from './AdminChangePassword';
import UserProfileSettings from './UserProfileSettings';
import { Button } from '@/components/ui/button';
import { Database, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../utils/tokenManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SYSTEM_INFO = [
  ['Platform Version', 'MyInvestFolio v1.0'],
  ['Authentication', 'JWT Token Based'],
  ['Security Model', 'Approval-Based Access'],
  ['2FA Status', 'Authenticator App'],
];

function SeedSamplePortfolio() {
  const [seeding, setSeeding] = useState(false);
  const [seeded, setSeeded] = useState(false);

  const handleSeed = async () => {
    if (!window.confirm('This will reset and re-seed the global Sample Portfolio. Continue?')) return;
    setSeeding(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/seed-sample-portfolio`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Sample portfolio seeded successfully');
        setSeeded(true);
      } else {
        toast.error(data.detail || 'Failed to seed sample portfolio');
      }
    } catch (err) {
      toast.error('Failed to seed sample portfolio');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700">
      <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Sample Portfolio</h3>
      <p className="text-sm text-gray-400 mb-4">
        Seed or refresh the global Sample Portfolio that all users can view. This creates 16 diversified investments (10 direct, 5 indirect, 1 related) across stocks, bonds, real estate, gold, ETFs, and more, with 2 entities (Demo Holdings Ltd, Demo Property Co) and 1 trust (Demo Family Trust).
      </p>
      <Button
        data-testid="seed-sample-portfolio-btn"
        onClick={handleSeed}
        disabled={seeding}
        className={seeded ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'}
      >
        {seeding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Seeding...</> :
          seeded ? <><CheckCircle className="w-4 h-4 mr-2" />Seeded Successfully</> :
          <><Database className="w-4 h-4 mr-2" />Seed Sample Portfolio</>}
      </Button>
    </div>
  );
}

const AdminSettingsTab = ({ admin, setAdmin }) => (
  <div className="space-y-6">
    <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700">
      <h3 className="text-lg sm:text-xl font-bold text-white mb-4">Admin Profile</h3>
      <div className="space-y-3">
        <div><label className="text-sm text-gray-400">Email</label><p className="text-white font-medium">{admin?.email}</p></div>
        <div><label className="text-sm text-gray-400">Name</label><p className="text-white font-medium">{admin?.name || 'Not set'}</p></div>
        <div><label className="text-sm text-gray-400">Role</label><p className="text-white font-medium">Administrator</p></div>
      </div>
    </div>
    <AdminChangePassword />
    <SeedSamplePortfolio />
    <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700">
      <h3 className="text-lg sm:text-xl font-bold text-white mb-4">Security Settings</h3>
      <div className="text-sm text-gray-400 mb-6">Manage your security preferences including password, 2FA, and login activity monitoring.</div>
      {admin && <div className="bg-gray-900 rounded-lg p-4"><UserProfileSettings user={admin} onUserUpdate={setAdmin} /></div>}
    </div>
    <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700">
      <h3 className="text-lg sm:text-xl font-bold text-white mb-4">System Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SYSTEM_INFO.map(([label, value]) => (
          <div key={label} className="bg-gray-900 rounded-lg p-4">
            <div className="text-sm text-gray-400">{label}</div>
            <div className="font-semibold text-lg text-white">{value}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default AdminSettingsTab;
